import SwapVertIcon from '@mui/icons-material/SwapVert';
import ThreePOutlinedIcon from '@mui/icons-material/ThreePOutlined';
import { styled, Grid, Box, Typography } from '@mui/material';
import { useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormProvider, useForm } from 'react-hook-form';
import {
  WsButton,
  WsElementWithPopOver,
  WsSelectObj,
  WsTypography,
  WsTypoVariants,
  WsColors,
} from 'wisestep-base';

import WsCheckmarkSelect from '@/components/WsCheckmarkSelect';
import WsMultiSelectSmartFilter from '@/components/WsMultiSelectSmartFilter';
import ErrorLogger from '@/providers/ErrorLogger';

export interface ISmsLogFilterProps {
  setMessageStatusFilter: (arg: string[] | null) => void;
  setDirectionFilter: (arg: string | null) => void;
}

export const StyledSmartFilterPopover = styled(Box)(() => ({
  minWidth: '24rem',
  paddingLeft: '0.5rem',
  paddingTop: '1rem',
  paddingBottom: '1rem',
}));

const SmsLogFilter = ({ setMessageStatusFilter, setDirectionFilter }: ISmsLogFilterProps) => {
  const handleApplyFilter = (values: any) => {
    values.message === 'Select' ? setDirectionFilter('') : setDirectionFilter(values.message);
    const valuesArray: string[] = values.messageStatus.map((status) => status.value);
    setMessageStatusFilter(valuesArray);
  };

  interface messageStatus {
    name: string;
    value: string;
  }
  const STATUSES: { [key: string]: messageStatus } = {
    SENT: { name: 'Sent', value: 'Sent' },
    DELIVERED: { name: 'Delivered', value: 'Delivered' },
    FAILED: { name: 'Failed', value: 'Failed' },
    PENDING: { name: 'Pending', value: 'Pending' },
  };
  const formOptions = {
    defaultValues: { message: 'Select', messageStatus: [] },
  };
  const methods = useForm(formOptions);
  const { watch, reset, setValue, handleSubmit } = methods;
  const [messageStatus] = useState<messageStatus[]>([
    STATUSES.SENT,
    STATUSES.DELIVERED,
    STATUSES.FAILED,
    STATUSES.PENDING,
  ]);
  const msgOptions: WsSelectObj[] = [
    {
      label: 'Select',
      value: 'Select',
    },
    {
      label: 'Inbound',
      value: 'INBOUND',
    },
    {
      label: 'Outbound',
      value: 'OUTBOUND',
    },
  ];
  return (
    <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
      <FormProvider {...methods}>
        <Box bgcolor={WsColors.White} borderRadius={3} px={2} py={1} mt={2} mb={2}>
          <Grid item xs={12} container>
            <Box pt={1} sx={{ color: WsColors.TheBlues500 }}>
              <SwapVertIcon />
            </Box>
            <Grid pl={1} item xs={3}>
              <Typography color={WsColors.TertiaryShades600} fontSize="10px" fontWeight="bold">
                Message Direction
              </Typography>
              <Box>
                <WsElementWithPopOver
                  element={
                    <WsTypography variant={WsTypoVariants.BODY2} cursor="pointer">
                      {watch('message') ?? 'Message Direction'}
                    </WsTypography>
                  }
                >
                  <Grid rowGap={3}>
                    {msgOptions.map((e, index) => (
                      <Box
                        key={index}
                        p={1}
                        sx={{ '&:hover': { background: WsColors.TheBlues500 } }}
                      >
                        <WsTypography
                          onClick={() => setValue('message', String(e.value))}
                          variant={WsTypoVariants.SUB_TITLE}
                          cursor="pointer"
                        >
                          {e.label}
                        </WsTypography>
                      </Box>
                    ))}
                  </Grid>
                </WsElementWithPopOver>
              </Box>
            </Grid>
            {watch('message') === 'OUTBOUND' && (
              <Grid pt={1} item xs={4}>
                <WsMultiSelectSmartFilter
                  label="Message Status"
                  name="messageStatus"
                  icon={<ThreePOutlinedIcon style={{ color: WsColors.TheBlues500 }} />}
                >
                  <WsCheckmarkSelect
                    name="messageStatus"
                    options={messageStatus}
                    getLabel={(option) => option.name}
                  />
                </WsMultiSelectSmartFilter>
              </Grid>
            )}

            <Grid item xs={1} p={1}>
              <WsButton variant="text" size={'small'} onClick={() => reset()}>
                Reset
              </WsButton>
            </Grid>
            <Grid item xs={1} p={1}>
              <WsButton
                variant="contained"
                size={'small'}
                onClick={handleSubmit(handleApplyFilter)}
              >
                Apply
              </WsButton>
            </Grid>
          </Grid>
        </Box>
      </FormProvider>
    </ErrorBoundary>
  );
};

export default SmsLogFilter;
